/**
 * Demo API Service
 * 
 * Service for interacting with the demo backend API.
 */

import api from './api/api';
import { DemoSession } from '../contexts/DemoContext';

export interface DemoSessionCreate {
  user_id?: string;
  initial_data?: Record<string, any>;
}

export interface DemoUserInput {
  text: string;
  metadata?: Record<string, any>;
}

export interface DemoCardAction {
  action: string;
  payload: Record<string, any>;
}

export interface DemoLeadCapture {
  name: string;
  email: string;
  company: string;
  role?: string;
  industry?: string;
  additional_data?: Record<string, any>;
}

export interface DemoResponse {
  response: {
    message: string;
    type: string;
    metadata: Record<string, any>;
    card_actions: any[];
  };
  session: DemoSession;
  scene_config?: Record<string, any>;
  scene_advanced?: boolean;
  thread_id?: string;
}

export interface DemoScenesResponse {
  scenes: Record<string, any>;
}

export class DemoAPI {
  private static baseUrl = '/demo';

  /**
   * Create a new demo session
   */
  static async createSession(userId?: string): Promise<DemoSession> {
    try {
      const response = await api.post(`${this.baseUrl}/sessions`, {
        user_id: userId,
        initial_data: {}
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to create demo session:', error);
      throw new Error('Failed to create demo session');
    }
  }

  /**
   * Get demo session by session key
   */
  static async getSession(sessionKey: string): Promise<DemoSession> {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionKey}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demo session:', error);
      throw new Error('Failed to get demo session');
    }
  }

  /**
   * Get current scene information including required inputs
   */
  static async getCurrentSceneInfo(sessionKey: string): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionKey}/scene-info`);
      return response.data;
    } catch (error) {
      console.error('Failed to get current scene info:', error);
      throw new Error('Failed to get current scene info');
    }
  }

  /**
   * Update demo session progress
   */
  static async updateProgress(
    sessionKey: string, 
    progressData: Record<string, any>
  ): Promise<DemoSession> {
    try {
      const response = await api.put(`${this.baseUrl}/sessions/${sessionKey}/progress`, progressData);
      return response.data;
    } catch (error) {
      console.error('Failed to update demo progress:', error);
      throw new Error('Failed to update demo progress');
    }
  }

  /**
   * Process user input and get AI response
   */
  static async processUserInput(sessionKey: string, userInput: string): Promise<DemoResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionKey}/input`, {
        text: userInput,
        metadata: {}
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to process user input:', error);
      throw new Error('Failed to process user input');
    }
  }

  /**
   * Advance to the next scene
   */
  static async advanceScene(sessionKey: string): Promise<DemoResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionKey}/advance`);
      return response.data;
    } catch (error) {
      console.error('Failed to advance scene:', error);
      throw new Error('Failed to advance scene');
    }
  }

  /**
   * Handle card-specific actions
   */
  static async handleCardAction(
    sessionKey: string, 
    action: string, 
    payload: Record<string, any>
  ): Promise<{ action_result: any; session: DemoSession }> {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionKey}/card-action`, {
        action,
        payload
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to handle card action:', error);
      throw new Error('Failed to handle card action');
    }
  }

  /**
   * Capture lead information
   */
  static async captureLead(
    sessionKey: string, 
    leadData: DemoLeadCapture
  ): Promise<{ lead: any; session: DemoSession }> {
    try {
      const response = await api.post(`${this.baseUrl}/sessions/${sessionKey}/capture-lead`, leadData);
      return response.data;
    } catch (error) {
      console.error('Failed to capture lead:', error);
      throw new Error('Failed to capture lead');
    }
  }

  /**
   * Get available demo scenes
   */
  static async getScenes(): Promise<Record<string, any>> {
    try {
      const response = await api.get(`${this.baseUrl}/scenes`);
      return response.data.scenes;
    } catch (error) {
      console.error('Failed to get demo scenes:', error);
      throw new Error('Failed to get demo scenes');
    }
  }

  /**
   * Get specific scene configuration
   */
  static async getSceneConfig(sceneId: number): Promise<Record<string, any>> {
    try {
      const response = await api.get(`${this.baseUrl}/scenes/${sceneId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get scene config:', error);
      throw new Error('Failed to get scene config');
    }
  }

  /**
   * Execute scene-specific logic
   */
  static async executeScene(
    sceneId: number, 
    executionData: Record<string, any>
  ): Promise<Record<string, any>> {
    try {
      const response = await api.post(`${this.baseUrl}/scenes/${sceneId}/execute`, executionData);
      return response.data;
    } catch (error) {
      console.error('Failed to execute scene:', error);
      throw new Error('Failed to execute scene');
    }
  }

  /**
   * Get demo analytics
   */
  static async getAnalytics(): Promise<Record<string, any>> {
    try {
      const response = await api.get(`${this.baseUrl}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demo analytics:', error);
      throw new Error('Failed to get demo analytics');
    }
  }

  /**
   * Get demo leads
   */
  static async getLeads(
    skip: number = 0, 
    limit: number = 100, 
    status?: string
  ): Promise<{ leads: any[]; total_count: number }> {
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString()
      });
      
      if (status) {
        params.append('status', status);
      }

      const response = await api.get(`${this.baseUrl}/leads?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demo leads:', error);
      throw new Error('Failed to get demo leads');
    }
  }

  /**
   * Get specific demo lead
   */
  static async getLead(leadId: string): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/leads/${leadId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get demo lead:', error);
      throw new Error('Failed to get demo lead');
    }
  }

  /**
   * Update lead status
   */
  static async updateLeadStatus(
    leadId: string, 
    statusData: Record<string, any>
  ): Promise<any> {
    try {
      const response = await api.put(`${this.baseUrl}/leads/${leadId}/status`, statusData);
      return response.data;
    } catch (error) {
      console.error('Failed to update lead status:', error);
      throw new Error('Failed to update lead status');
    }
  }

  /**
   * Delete demo session
   */
  static async deleteSession(sessionKey: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`${this.baseUrl}/sessions/${sessionKey}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete demo session:', error);
      throw new Error('Failed to delete demo session');
    }
  }

  /**
   * Get chat messages for a demo session
   */
  static async getChatMessages(sessionKey: string): Promise<{
    messages: Array<{
      id: string;
      text: string;         // Changed from 'content' to 'text'
      type: string;          // Changed from 'message_type' to 'type'
      who: 'user' | 'assistant';  // Changed from 'sender_type' to 'who'
      metadata: any;
      created_at: string;
    }>;
    thread_id: string | null;
    thread_name: string | null;
  }> {
    try {
      const response = await api.get(`${this.baseUrl}/sessions/${sessionKey}/chat-messages`);
      return response.data;
    } catch (error) {
      console.error('Failed to get chat messages:', error);
      throw error;
    }
  }

  /**
   * Get help content for a specific scene
   */
  static async getSceneHelpContent(sessionKey: string, sceneId: number): Promise<{
    scene_id: number;
    scene_title: string;
    help_content: {
      title: string;
      content: string[];
    };
    is_current_scene: boolean;
    is_completed: boolean;
  }> {
    try {
      const url = `${this.baseUrl}/sessions/${sessionKey}/scenes/${sceneId}/help`;
      console.log('🔍 [DemoAPI] Making request to:', url);
      const response = await api.get(url);
      console.log('🔍 [DemoAPI] Response received:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🔍 [DemoAPI] Failed to get scene help content:', error);
      console.error('🔍 [DemoAPI] Error details:', error.response?.data);
      throw new Error('Failed to get scene help content');
    }
  }

  /**
   * Health check for demo system
   */
  static async healthCheck(): Promise<Record<string, any>> {
    try {
      const response = await api.get(`${this.baseUrl}/health`);
      return response.data;
    } catch (error) {
      console.error('Demo health check failed:', error);
      throw new Error('Demo health check failed');
    }
  }

  /**
   * Generate threads for all demo scenes
   */
  static async generateAllSceneThreads(sessionKey: string, instantMode: boolean = true): Promise<{
    success: boolean;
    total_scenes: number;
    created_count: number;
    results: Array<{
      scene: number;
      success: boolean;
      thread_id?: string;
      thread_name?: string;
      message_count?: number;
      error?: string;
    }>;
  }> {
    try {
      const response = await api.post(`${this.baseUrl}/scenes/generate-all`, null, {
        params: {
          session_key: sessionKey,
          instant_mode: instantMode
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate scene threads:', error);
      throw new Error('Failed to generate scene threads');
    }
  }
}

export default DemoAPI;
